import React from "react";
import { SelectList } from "web-ui";
import { LegacyButton as Button } from "web-ui/legacy";

import type {
  PhonemeSpan,
  Span,
  VisualizationResponse,
} from "../api/transcription";
import styles from "../styles/Visualization.module.scss";
import { partitionsToSpans } from "../util/partitionsToSpans";

import { AudioWave } from "./AudioWave/AudioWave";
import type { Partition } from "./AudioWave/AudioWave";
import { HelpButton } from "./HelpButton";

const ANNOTATION_EDITOR_HEADER = "Annotation Editor";
const ANOTATION_EDITOR_INSTRUCTIONS =
  "Use this tool to adjust timings or content of phonemes and words.\n\n" +
  "Click on the waveform to add a phoneme or word.\n" +
  "Click on a phoneme or word label to edit it.\n" +
  "Drag the lines of a phoneme or word to adjust its timing.\n\n" +
  'Don\'t forget to click the "Save" button to save your changes.';
const NOTE = 'NOTE: "SIL" replaced with "_" for clarity.';
const SAVE = "save";
const WAVEFORM_SCALING_LEAD_IN_TEXT = "Waveform Scale Factor: ";

const WAVEFORM_SCALE_FACTORS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const DEFAULT_WAVEFORM_SCALE_FACTOR = 1;

/**
 * Visualization visualizes and allows the editing of word and phoneme partitions.
 *
 * @param props is described below.
 * @param props.audioRef is a reference to the audio that is being visualized. Used to control the audio cursor.
 * @param props.updatePhonemeSpans is called when phoneme spans from visualizationData are edited. The times are actual timestamps, NOT in the range 0 - 1.
 * @param props.updateWordSpans is called when word spans from visualizationData are edited. The times are actual timestamps, NOT in the range 0 - 1.
 * @param props.visemeMapping is a mapping from phonemes to visemes.
 * @param props.visualizationData contains the waveform amplitude and phoneme/word annotation data that should be displayed and edited. The timestamps on annotations should be actual timestamps, NOT in the range 0 - 1.
 * @param props.onSaveAnnotations is called when the save button is pressed.
 * @param props.isSaving controls the save button. If true, then the button is disabled and shows a loading icon.
 */
export const Visualization = ({
  audioRef,
  updatePhonemeSpans,
  updateWordSpans,
  visemeMapping,
  visualizationData,
  onSaveAnnotations,
  isSaving,
}: {
  audioRef: React.MutableRefObject<HTMLAudioElement | undefined>;
  visemeMapping: Record<string, string | undefined>;
  visualizationData: VisualizationResponse;
  updatePhonemeSpans: (phonemeSpans: PhonemeSpan[]) => void;
  updateWordSpans: (wordSpans: Span[]) => void;
  onSaveAnnotations: () => void;
  isSaving: boolean;
}) => {
  const amplitudes = visualizationData.amplitudes;
  const audioDuration = visualizationData.audio_duration;

  const [wordStarts, setWordStarts] = React.useState<Partition[]>([]);
  const [phonemeStarts, setPhonemeStarts] = React.useState<Partition[]>([]);
  const [waveformScaleFactor, setWaveformScaleFactor] = React.useState(
    DEFAULT_WAVEFORM_SCALE_FACTOR,
  );

  React.useEffect(() => {
    setWordStarts(
      visualizationData.annotation.spans.flatMap(
        (span: Span): Partition => ({
          label: span.text,
          spanData: span,
          time: span.start_time / audioDuration,
        }),
      ),
    );

    setPhonemeStarts(
      visualizationData.annotation.phoneme_spans?.flatMap(
        (phonemeSpan: PhonemeSpan) => ({
          label: phonemeSpan.phoneme,
          spanData: phonemeSpan,
          time: phonemeSpan.start_time / audioDuration,
        }),
      ) ?? [],
    );
  }, [visualizationData, audioDuration]);

  const addPhoneme = (time: number, phoneme: string) => {
    const convertedNewPhoneme = phoneme === "_" ? "SIL" : phoneme;
    const newViseme = visemeMapping[convertedNewPhoneme];
    if (newViseme === undefined) {
      alert(
        `Viseme mapping is missing a viseme for phoneme: ${convertedNewPhoneme}. Is this a valid phoneme? Change failed.`,
      );
      throw new Error(
        `Viseme mapping is missing a viseme for phoneme: ${convertedNewPhoneme}. Is this a valid phoneme?`,
      );
    }
    // Phoneme span timing gets created by the call to partitionsToSpans based on the Partition time,
    // so we just include it with fake data for the type checker.
    const updatedPhonemeSpanDataWithoutUpdatedTiming = {
      end_time: NaN,
      phoneme: convertedNewPhoneme,
      // Human confidence is the maximum confidence, which on the log scale is 0
      score: 0,
      start_time: NaN,
      viseme: +newViseme,
    };
    const partiallyUpdatedPhonemePartition = {
      label: phoneme,
      spanData: updatedPhonemeSpanDataWithoutUpdatedTiming,
      time,
    };

    const newPhonemeStarts = [
      ...phonemeStarts,
      partiallyUpdatedPhonemePartition,
    ];
    updatePhonemes(newPhonemeStarts);
  };

  const addWord = (time: number, word: string) => {
    // Word span timing gets created by the call to partitionsToSpans based on the Partition time,
    // so we just include it with fake data for the type checker. Similarly, pronunciations get created
    // by the call to updateWords.
    const wordSpanDataWithNewTextAndScore = {
      end_time: NaN,
      pronunciations: [],
      // Human confidence is the maximum confidence, which on the log scale is 0
      score: 0,
      start_time: NaN,
      text: word,
    };
    const partiallyUpdatedWordPartition = {
      label: word,
      spanData: wordSpanDataWithNewTextAndScore,
      time,
    };

    const newWordStarts = [...wordStarts, partiallyUpdatedWordPartition];
    updateWords(newWordStarts);
  };

  const deletePhoneme = (index: number) => {
    const newPhonemeStarts = [
      ...phonemeStarts.slice(0, index),
      ...phonemeStarts.slice(index + 1),
    ];
    updatePhonemes(newPhonemeStarts);
  };

  const editPhoneme = (
    index: number,
    newTime: number | undefined,
    newPhoneme: string | undefined,
  ) => {
    const existingPhonemePartition = phonemeStarts[index];
    const existingSpanData = existingPhonemePartition.spanData;
    if (!("phoneme" in existingSpanData)) {
      alert("Edit phoneme function called on a word partition. Change failed.");
      throw new Error("Edit phoneme function called on a word partition.");
    }
    const convertedNewPhoneme = newPhoneme === "_" ? "SIL" : newPhoneme;
    const newViseme =
      convertedNewPhoneme === undefined
        ? existingSpanData.viseme.toString()
        : visemeMapping[convertedNewPhoneme];
    if (newViseme === undefined) {
      alert(
        `Viseme mapping is missing a viseme for phoneme: ${convertedNewPhoneme}. Is this a valid phoneme? Change failed.`,
      );
      throw new Error(
        `Viseme mapping is missing a viseme for phoneme: ${convertedNewPhoneme}. Is this a valid phoneme?`,
      );
    }
    // Phoneme span timing gets updated by the call to partitionsToSpans based on the Partition time,
    // so we don't need to update it here.
    const updatedPhonemeSpanDataWithoutUpdatedTiming = {
      ...existingSpanData,
      phoneme: convertedNewPhoneme ?? existingSpanData.phoneme,
      // Human confidence is the maximum confidence, which on the log scale is 0
      score: convertedNewPhoneme ? 0 : existingSpanData.score,
      viseme: convertedNewPhoneme ? +newViseme : existingSpanData.viseme,
    };
    const partiallyUpdatedPhonemePartition: Partition = {
      ...existingPhonemePartition,
      spanData: updatedPhonemeSpanDataWithoutUpdatedTiming,
      time: newTime ?? existingPhonemePartition.time,
    };

    const newPhonemeStarts = [
      ...phonemeStarts.slice(0, index),
      partiallyUpdatedPhonemePartition,
      ...phonemeStarts.slice(index + 1),
    ];
    updatePhonemes(newPhonemeStarts);
  };

  const updatePhonemes = (newPhonemeStarts: Partition[]) => {
    setPhonemeStarts(newPhonemeStarts);

    const phonemeSpans = partitionsToSpans(
      newPhonemeStarts,
      audioDuration,
    ) as PhonemeSpan[];
    updatePhonemeSpans(phonemeSpans);

    // Changing phonemes changes the pronunciation of the word,
    // so we need to update the word spans.
    fixPronunciationsAndUpdateWordSpans(
      visualizationData.annotation.spans,
      phonemeSpans,
    );
  };

  const deleteWord = (index: number) => {
    const newWordStarts = [
      ...wordStarts.slice(0, index),
      ...wordStarts.slice(index + 1),
    ];
    updateWords(newWordStarts);
  };

  const editWord = (
    index: number,
    newTime: number | undefined,
    newText: string | undefined,
  ) => {
    const existingWordPartition = wordStarts[index];
    const existingSpanData = existingWordPartition.spanData;
    if (!("text" in existingSpanData)) {
      alert("Edit word function called on a phoneme partition. Change failed.");
      throw new Error("Edit word function called on a phoneme partition.");
    }

    // Word span timing gets updated by the call to partitionsToSpans based on the Partition time,
    // so we don't need to update it here.
    const wordSpanDataWithNewTextAndScore = {
      ...existingSpanData,
      // Human confidence is the maximum confidence, which on the log scale is 0
      score: newText ? 0 : existingSpanData.score,
      text: newText ?? existingSpanData.text,
    };
    const partiallyUpdatedWordPartition = {
      ...existingWordPartition,
      label: newText ?? existingWordPartition.label,
      spanData: wordSpanDataWithNewTextAndScore,
      time: newTime ?? existingWordPartition.time,
    };

    const newWordStarts = [
      ...wordStarts.slice(0, index),
      partiallyUpdatedWordPartition,
      ...wordStarts.slice(index + 1),
    ];
    updateWords(newWordStarts);
  };

  const updateWords = (newWordStarts: Partition[]) => {
    setWordStarts(newWordStarts);
    const wordSpans = partitionsToSpans(newWordStarts, audioDuration) as Span[];
    fixPronunciationsAndUpdateWordSpans(
      wordSpans,
      visualizationData.annotation.phoneme_spans ?? [],
    );
  };

  const fixPronunciationsAndUpdateWordSpans = (
    wordSpans: Span[],
    phonemeSpans: PhonemeSpan[],
  ) => {
    const fixedPronunciationWordSpans = wordSpans.map(span => {
      const isHalfContained = (phonemeSpan: PhonemeSpan) => {
        const phonemeSpanMidpoint =
          (phonemeSpan.start_time + phonemeSpan.end_time) / 2;
        return (
          span.start_time <= phonemeSpanMidpoint &&
          phonemeSpanMidpoint <= span.end_time
        );
      };

      return {
        ...span,
        pronunciations: [
          (
            phonemeSpans
              .filter(isHalfContained)
              .map(phonemeSpan => phonemeSpan.phoneme) ?? []
          ).join(" "),
        ],
      };
    });

    updateWordSpans(fixedPronunciationWordSpans);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.horizontal}>
        <HelpButton instructions={ANOTATION_EDITOR_INSTRUCTIONS} />
        <h2>{ANNOTATION_EDITOR_HEADER}</h2>
        {WAVEFORM_SCALING_LEAD_IN_TEXT}
        <SelectList
          items={WAVEFORM_SCALE_FACTORS.map(factor => ({
            text: factor.toString(),
          }))}
          onChange={e =>
            setWaveformScaleFactor(WAVEFORM_SCALE_FACTORS[e.selectedIndices[0]])
          }
          popoverPosition={{
            direction: "down",
            manualPositioning: true,
            style: {
              marginTop: 50,
              maxHeight: 150,
              position: "absolute",
            },
            zIndex: 51,
          }}
          selectedIndices={[
            WAVEFORM_SCALE_FACTORS.indexOf(waveformScaleFactor),
          ]}
        />
      </div>
      <AudioWave
        addPhonemePartition={addPhoneme}
        addWordPartition={addWord}
        amplitudes={amplitudes}
        audioRef={audioRef}
        deletePhonemePartition={deletePhoneme}
        deleteWordPartition={deleteWord}
        editPhonemePartition={editPhoneme}
        editWordPartition={editWord}
        phonemePartitions={phonemeStarts}
        waveformScaleFactor={waveformScaleFactor}
        wordPartitions={wordStarts}
      />
      <p className={styles["note"]}>{NOTE}</p>
      <div>
        <Button
          color="macaw"
          onClick={onSaveAnnotations}
          submitting={isSaving}
          type="submit"
          variant="solid"
        >
          {SAVE}
        </Button>
      </div>
    </div>
  );
};
